
import { FantasyRuleResult,
  BaseFantasyRule, BaseFantasyRuleRange,
  RuleResultGood, RuleResultAlreadySelected } from './rule'

export class UniquePlayerRule extends BaseFantasyRule {
  constructor (label, names) {
    super(label)
    this.showInRules = false
  }

  canSelect (playerKey, selectionState) {
    const playerState = selectionState.playersState[playerKey]
    if (playerState.selected > -1) {
      return RuleResultAlreadySelected
    }

    return RuleResultGood
  }

  isFulfilled (selectionState) {
    const isUnique = selectionState.playerKeys.every((e, i, a) => a.indexOf(e) === i)
    if (!isUnique) {
      return new FantasyRuleResult(false, 'Duplicate players not allowed', 560)
    }
    return RuleResultGood
  }
}

export class PlayersCountRule extends BaseFantasyRuleRange {
  fulfilledValue (selectionState, playerKey) {
    return selectionState.playerKeys.length
  }
}

export class BoostPlayerRule extends BaseFantasyRule {
  constructor (label, names) {
    super(label)
    this.names = names
  }

  get hasFulfillmentLabel () {
    return this.names.length > 0
  }

  get fulfillmentLabel () {
    return this.names.join(', ')
  }

  isFulfilled (selectionState) {
    const boostPlayers = selectionState.selectedBoostPlayers()
    for (let i = 0; i < selectionState.rule.boost_names.length; i++) {
      const name = selectionState.rule.boost_names[i]
      const count = boostPlayers[i]
      if (!count) {
        return new FantasyRuleResult(false, `Must select a "${name}" from your players`)
      }
    }
    return RuleResultGood
  }
}

export class CreditsRule extends BaseFantasyRuleRange {
  fulfilled (selectionState, suffix) {
    const val = super.fulfilled(selectionState, suffix)
    const balance = selectionState.rule.credits - this.fulfilledValue(selectionState)
    return `(${balance}) ${val}`
  }

  canSelect (playerKey, selectionState) {
    const player = selectionState.playersState[playerKey].player
    const creditRequired = player.credits
    const totalRequired = selectionState.creditsUsedTotal + creditRequired
    if (totalRequired > selectionState.rule.credits) {
      const req = totalRequired - selectionState.rule.credits
      return new FantasyRuleResult(false, `You need ${req} more credits.`)
    }
    return RuleResultGood
  }

  fulfilledValue (selectionState, playerKey) {
    return selectionState.creditsUsedTotal
  }

  rangeUpperError (value, expected, relatedName) {
    return new FantasyRuleResult(false, `Used more than ${expected} credits`)
  }
}

export class PlayerPerTeamRule extends BaseFantasyRuleRange {
  fulfilled (selectionState, suffix) {
    return ''
  }

  fulfilledValue (selectionState, playerKey) {
    if (playerKey) {
      const playerState = selectionState.playersState[playerKey]
      const player = playerState.player
      return selectionState.teamPlayersCount[player.team.key]
    }

    return selectionState.teamPlayersCount
  }

  canSelectError (value, expected) {
    return new FantasyRuleResult(false, `${value} players already selected from same team`, 561)
  }
}

export class PlayerPerMatchRule extends BaseFantasyRuleRange {
  fulfilled (selectionState, suffix) {
    return ''
  }

  canSelect (playerKey, selectionState) {
    const upper = this.range[1]
    const player = selectionState.playersState[playerKey].player
    let matchCountOkay = 0
    let matchCountMsg = ''

    for (const matchKey of player.all_matches) {
      if (upper && selectionState.matchPlayersCount[matchKey] && selectionState.matchPlayersCount[matchKey] >= upper) {
        matchCountMsg = `Already selected ${upper} players from the same match`
      } else {
        matchCountOkay += 1
      }
    }

    if (matchCountOkay === 0) {
      return new FantasyRuleResult(false, matchCountMsg)
    }
    return RuleResultGood
  }

  fulfilledValue (selectionState, playerKey) {
    return selectionState.matchPlayersCount
  }
}

export class TeamCountRule extends BaseFantasyRuleRange {
  fulfilledValue (selectionState, playerKey) {
    return Object(selectionState.teamPlayersCount).length
  }

  canSelect (playerKey, selectionState) {
    const player = selectionState.playersState[playerKey].player
    if (!selectionState.teamPlayersCount[player.team.key]) {
      const upper = this.range[1]
      const count = this.fulfilledValue(selectionState, playerKey)
      if (upper && count >= upper) {
        return this.canSelectError(count, upper)
      }
    }
    return RuleResultGood
  }
}

export class MatchCountRule extends BaseFantasyRuleRange {
  canSelect (playerKey, selectionState) {
    const upper = this.range[1]
    const player = selectionState.playersState[playerKey].player
    let matchCountOkay = 0

    for (const matchKey of player.all_matches) {
      if (selectionState.matchPlayersCount[matchKey] && selectionState.matchPlayersCount[matchKey] < upper) {
        matchCountOkay += 1
      }
    }

    if (upper && matchCountOkay === 0) {
      return new FantasyRuleResult(false, `Already selected players from ${upper} matches`)
    }

    return RuleResultGood
  }

  fulfilledValue (selectionState, playerKey) {
    return Object.keys(selectionState.matchPlayersCount).length
  }
}

export class RoleCountRule extends BaseFantasyRuleRange {
  constructor (label, minValue, maxValue, role) {
    super(label, minValue, maxValue)
    this.role = role
  }

  fulfilledValue (selectionState, playerKey) {
    if (playerKey) {
      const playerState = selectionState.playersState[playerKey]
      const player = playerState.player
      const role = player.primary_role
      if (role !== this.role) {
        return 0
      }
    }

    return selectionState.rolesCount[this.role]
  }
}
